/**
 * Conceptos Facturas (CRUD)
 * Capítulos con sub-conceptos. Editar inline, agregar, eliminar.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './ConceptosFacturas.css';

const QUERY = `{
  conceptosFactura {
    id nombre orden activo
    conceptos { id capitulo_id nombre orden activo puede_repetir }
  }
}`;

const ConceptosFacturas = () => {
  const { success, error } = useNotification();
  const [capitulos, setCapitulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandidos, setExpandidos] = useState({});
  const [editando, setEditando] = useState(null); // { tipo: 'cap'|'con', id, valor }
  const [agregandoCap, setAgregandoCap] = useState(false);
  const [nuevoCapNombre, setNuevoCapNombre] = useState('');
  const [agregandoConcepto, setAgregandoConcepto] = useState(null); // capitulo_id
  const [nuevoConceptoNombre, setNuevoConceptoNombre] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.query(QUERY);
      setCapitulos(r.conceptosFactura || []);
    } catch (e) {
      error('Error al cargar: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => { cargar(); }, [cargar]);

  const toggle = (id) => setExpandidos(p => ({ ...p, [id]: !p[id] }));

  // === Capítulos ===
  const crearCapitulo = async () => {
    const nombre = nuevoCapNombre.trim();
    if (!nombre) return;
    try {
      const orden = (capitulos[capitulos.length - 1]?.orden || 0) + 1;
      const r = await client.mutate(
        `mutation($n: String!, $o: Int) { crearConceptoCapitulo(nombre: $n, orden: $o) { success message id } }`,
        { n: nombre, o: orden }
      );
      if (r.crearConceptoCapitulo.success) {
        success('Capítulo creado');
        setNuevoCapNombre('');
        setAgregandoCap(false);
        cargar();
      } else {
        error(r.crearConceptoCapitulo.message);
      }
    } catch (e) { error(e.message); }
  };

  const editarCapitulo = async (id, nombre) => {
    if (!nombre.trim()) return;
    try {
      await client.mutate(
        `mutation($id: Int!, $n: String) { editarConceptoCapitulo(id: $id, nombre: $n) { success message } }`,
        { id, n: nombre.trim() }
      );
      success('Actualizado');
      setEditando(null);
      cargar();
    } catch (e) { error(e.message); }
  };

  const eliminarCapitulo = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar capítulo "${nombre}" y todos sus conceptos?`)) return;
    try {
      await client.mutate(`mutation($id: Int!) { eliminarConceptoCapitulo(id: $id) { success message } }`, { id });
      success('Capítulo eliminado');
      cargar();
    } catch (e) { error(e.message); }
  };

  // === Conceptos ===
  const crearConcepto = async (capituloId) => {
    const nombre = nuevoConceptoNombre.trim();
    if (!nombre) return;
    try {
      const cap = capitulos.find(c => c.id === capituloId);
      const orden = ((cap?.conceptos || []).slice(-1)[0]?.orden || 0) + 1;
      const r = await client.mutate(
        `mutation($cid: Int!, $n: String!, $o: Int) { crearConceptoFactura(capitulo_id: $cid, nombre: $n, orden: $o) { success message id } }`,
        { cid: capituloId, n: nombre, o: orden }
      );
      if (r.crearConceptoFactura.success) {
        success('Concepto creado');
        setNuevoConceptoNombre('');
        setAgregandoConcepto(null);
        cargar();
      } else {
        error(r.crearConceptoFactura.message);
      }
    } catch (e) { error(e.message); }
  };

  const editarConcepto = async (id, nombre) => {
    if (!nombre.trim()) return;
    try {
      await client.mutate(
        `mutation($id: Int!, $n: String) { editarConceptoFactura(id: $id, nombre: $n) { success message } }`,
        { id, n: nombre.trim() }
      );
      success('Concepto actualizado');
      setEditando(null);
      cargar();
    } catch (e) { error(e.message); }
  };

  const eliminarConcepto = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar concepto "${nombre}"?`)) return;
    try {
      await client.mutate(`mutation($id: Int!) { eliminarConceptoFactura(id: $id) { success message } }`, { id });
      success('Concepto eliminado');
      cargar();
    } catch (e) { error(e.message); }
  };

  const togglePuedeRepetir = async (id, valorActual) => {
    const nuevoValor = !valorActual;
    setCapitulos(prev => prev.map(cap => ({
      ...cap,
      conceptos: cap.conceptos.map(co => co.id === id ? { ...co, puede_repetir: nuevoValor } : co)
    })));
    try {
      await client.mutate(
        `mutation($id: Int!, $pr: Boolean) { editarConceptoFactura(id: $id, puede_repetir: $pr) { success message } }`,
        { id, pr: nuevoValor }
      );
    } catch (e) {
      setCapitulos(prev => prev.map(cap => ({
        ...cap,
        conceptos: cap.conceptos.map(co => co.id === id ? { ...co, puede_repetir: valorActual } : co)
      })));
      error(e.message);
    }
  };

  if (loading) return <div className="cf-loading">Cargando…</div>;

  return (
    <div className="cf-container">
      <div className="cf-header">
        <div>
          <h1>Conceptos de Facturas</h1>
          <p className="cf-sub">Capítulos y sub-conceptos para clasificar facturas</p>
        </div>
        <button className="cf-btn cf-btn-primary" onClick={() => setAgregandoCap(true)}>+ Nuevo Capítulo</button>
      </div>

      {agregandoCap && (
        <div className="cf-cap-nuevo">
          <input
            type="text"
            placeholder="Nombre del capítulo (ej: ACABADOS)"
            value={nuevoCapNombre}
            onChange={(e) => setNuevoCapNombre(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') crearCapitulo(); if (e.key === 'Escape') { setAgregandoCap(false); setNuevoCapNombre(''); } }}
            autoFocus
          />
          <button className="cf-btn cf-btn-primary" onClick={crearCapitulo}>Crear</button>
          <button className="cf-btn" onClick={() => { setAgregandoCap(false); setNuevoCapNombre(''); }}>Cancelar</button>
        </div>
      )}

      <div className="cf-list">
        {capitulos.length === 0 ? (
          <div className="cf-empty">Sin capítulos. Crea el primero.</div>
        ) : capitulos.map(cap => {
          const abierto = !!expandidos[cap.id];
          return (
            <div key={cap.id} className={`cf-cap ${abierto ? 'cf-cap-open' : ''}`}>
              <div className="cf-cap-head">
                <div className="cf-cap-title-wrap">
                  <span className="cf-chev" onClick={() => toggle(cap.id)} style={{ cursor: 'pointer' }}>{abierto ? '▼' : '▶'}</span>
                  {editando?.tipo === 'cap' && editando.id === cap.id ? (
                    <input
                      type="text"
                      value={editando.valor}
                      onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                      onBlur={() => editarCapitulo(cap.id, editando.valor)}
                      onKeyDown={(e) => { if (e.key === 'Enter') editarCapitulo(cap.id, editando.valor); if (e.key === 'Escape') setEditando(null); }}
                      autoFocus
                      className="cf-input-edit"
                    />
                  ) : (
                    <strong onClick={() => toggle(cap.id)} style={{ cursor: 'pointer' }}>{cap.nombre}</strong>
                  )}
                  <span className="cf-count">{cap.conceptos.length}</span>
                </div>
                <div className="cf-cap-actions">
                  <button className="cf-btn-icon" onClick={() => setEditando({ tipo: 'cap', id: cap.id, valor: cap.nombre })} title="Editar">✏️</button>
                  <button className="cf-btn-icon cf-btn-danger" onClick={() => eliminarCapitulo(cap.id, cap.nombre)} title="Eliminar">🗑️</button>
                </div>
              </div>
              {abierto && (
                <div className="cf-cap-body">
                  {cap.conceptos.length === 0 ? (
                    <div className="cf-empty-small">Sin conceptos</div>
                  ) : cap.conceptos.map(co => (
                    <div key={co.id} className="cf-concepto">
                      {editando?.tipo === 'con' && editando.id === co.id ? (
                        <input
                          type="text"
                          value={editando.valor}
                          onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                          onBlur={() => editarConcepto(co.id, editando.valor)}
                          onKeyDown={(e) => { if (e.key === 'Enter') editarConcepto(co.id, editando.valor); if (e.key === 'Escape') setEditando(null); }}
                          autoFocus
                          className="cf-input-edit"
                        />
                      ) : (
                        <span className="cf-concepto-name">
                          {co.nombre}
                          <button
                            type="button"
                            className={`cf-repetir-btn ${co.puede_repetir !== false ? 'repetible' : 'unico'}`}
                            onClick={() => togglePuedeRepetir(co.id, co.puede_repetir !== false)}
                            title={co.puede_repetir !== false ? 'Click para marcar como único (no repetible)' : 'Click para permitir repetición'}
                          >
                            {co.puede_repetir !== false ? '↻ Repetible' : '⊘ Único'}
                          </button>
                        </span>
                      )}
                      <div className="cf-concepto-actions">
                        <button className="cf-btn-icon" onClick={() => setEditando({ tipo: 'con', id: co.id, valor: co.nombre })} title="Editar">✏️</button>
                        <button className="cf-btn-icon cf-btn-danger" onClick={() => eliminarConcepto(co.id, co.nombre)} title="Eliminar">🗑️</button>
                      </div>
                    </div>
                  ))}
                  {agregandoConcepto === cap.id ? (
                    <div className="cf-concepto-nuevo">
                      <input
                        type="text"
                        placeholder="Nuevo concepto"
                        value={nuevoConceptoNombre}
                        onChange={(e) => setNuevoConceptoNombre(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') crearConcepto(cap.id); if (e.key === 'Escape') { setAgregandoConcepto(null); setNuevoConceptoNombre(''); } }}
                        autoFocus
                      />
                      <button className="cf-btn cf-btn-sm cf-btn-primary" onClick={() => crearConcepto(cap.id)}>Agregar</button>
                      <button className="cf-btn cf-btn-sm" onClick={() => { setAgregandoConcepto(null); setNuevoConceptoNombre(''); }}>Cancelar</button>
                    </div>
                  ) : (
                    <button className="cf-btn-add-sub" onClick={() => setAgregandoConcepto(cap.id)}>+ Agregar concepto</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConceptosFacturas;
